import type { ReactNode } from "react";
import { useLocale } from "use-intl";

import { priceParts, type PriceParts } from "@/lib/priceParts";
import { cn } from "@/lib/utils";

function PriceSup({ children, side }: { children: ReactNode; side: "before" | "after" }) {
  return (
    <span
      className={side === "before" ? "price-sup price-sup-before" : "price-sup price-sup-after"}
    >
      <span>{children}</span>
    </span>
  );
}

function plainPriceText(parts: PriceParts) {
  return [
    parts.sign,
    parts.symbolBefore && `${parts.symbolBefore}\u00a0`,
    parts.whole,
    parts.fraction && `${parts.decimal}${parts.fraction}`,
    parts.symbolAfter && `\u00a0${parts.symbolAfter}`,
  ]
    .filter(Boolean)
    .join("");
}

function PriceSuperscripts({ parts }: { parts: PriceParts }) {
  return (
    <>
      {parts.sign}
      {parts.symbolBefore && <PriceSup side="before">{parts.symbolBefore}</PriceSup>}
      {parts.whole}
      {parts.fraction && (
        <>
          <span className="sr-only">{parts.decimal}</span>
          <PriceSup side="after">{parts.fraction}</PriceSup>
        </>
      )}
      {parts.symbolAfter && <PriceSup side="after">{parts.symbolAfter}</PriceSup>}
    </>
  );
}

/**
 * The one way the app renders a money amount: the currency symbol and the cents as small
 * cap-aligned superscripts beside a full-size whole part (R$249⁴⁴). The decimal separator stays in
 * the text for screen readers and copy/paste. Renders `fallback` when the amount is missing.
 *
 * Pass `plain` for tiny labels (about 12px and below): the superscripts hit their 9px floor there and
 * stop reading as one price, so it renders the ordinary inline form (R$ 365,56) instead.
 *
 * Use the string formatters in `@/lib/format-price` only where markup cannot go (aria labels,
 * titles, SEO and message text).
 */
export function Price({
  amount,
  currency,
  compact = false,
  plain = false,
  fallback = null,
  className,
}: {
  amount: string | number | null | undefined;
  currency: string | null | undefined;
  /** Whole units only (truncated), for badges and dense cards. */
  compact?: boolean;
  /** Ordinary inline form (R$ 365,56) for tiny labels. */
  plain?: boolean;
  fallback?: ReactNode;
  className?: string;
}) {
  const locale = useLocale();
  const parts = priceParts(amount, currency, locale, { compact });
  if (!parts) return fallback;
  return (
    <span className={cn("whitespace-nowrap tabular-nums", className)}>
      {plain ? plainPriceText(parts) : <PriceSuperscripts parts={parts} />}
    </span>
  );
}
